import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import { useDefinedParams } from '@/hooks/useDefinedParams';
import { useJbpQuery, RestTypes } from '@/hooks/useJbpQuery';
import { useBookingPayload } from '@/hooks/useBookingPayload';
import { ApiError } from '@/elements/ApiError';
import { BookingResponse } from '../../../../../APIDocs/apiTypes';
import { BookingSuccess } from './BookingSuccess';
import { BookingResultLoading } from '@/elements/loadingElements/BookingResultLoading';
import { useQueryClient } from '@tanstack/react-query';
import { clearJobDetails, setSelectedOffer } from '@/reducers/jbpGeneralSlice';
import { useNavigate } from 'react-router-dom';

export const BookingResult = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mpan, serviceLine] = useDefinedParams(['mpan', 'serviceLine']);
  const state = useAppSelector(state => state.jbp);
  const [copiedState] = useState(state);

  useEffect(() => {
    if (!state.jobDetails || !state.offer) {
      // redirect to site page if job details or selected offer are not present
      if (mpan && serviceLine) {
        navigate(`/site/${mpan}/${serviceLine}`);
        return;
      }
      navigate('/');
    }

    queryClient.invalidateQueries({ queryKey: [`site-${mpan}-${serviceLine}-${state.contractId}`] });
    queryClient.invalidateQueries({ queryKey: [`get-booking-list-${mpan}-${serviceLine}`] });
    if (shouldPostBookingRequest === true) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bookingPayload = useBookingPayload(copiedState);

  const handleRequestComplete = () => {
    dispatch(clearJobDetails());
    dispatch(setSelectedOffer(undefined));
  };
  const { isLoading, data, refetch } = useJbpQuery<BookingResponse, BookingResponse>(
    RestTypes.Post,
    bookingPayload,
    ['booking'],
    `jbp/${mpan}/booking`,
    false,
    serviceLine,
    handleRequestComplete,
    handleRequestComplete
  );
  const shouldPostBookingRequest: boolean | undefined = copiedState.offer?.offerTokenSelected;

  let backButtonPrevented = false;
  history.pushState(null, document.title, location.href);

  function popStateListener() {
    if (backButtonPrevented === false) {
      history.pushState(null, document.title, location.href);
      backButtonPrevented = true;
      navigate(`/site/${mpan}/${serviceLine}`);
    } else {
      window.removeEventListener('popstate', popStateListener);
    }
  }

  window.addEventListener('popstate', popStateListener);

  if (shouldPostBookingRequest == null || shouldPostBookingRequest == false) {
    return <ApiError errorDescription={'You have not completed the questions required to confirm your booking. Please return to the site page and follow the book job process '}></ApiError>;
  } else if (isLoading) {
    return <BookingResultLoading />;
  } else {
    if (data?.Status == 'Success' && data?.ConfirmationID != undefined) {
      return (
        <div>
          <BookingSuccess bookingId={data.ConfirmationID} state={copiedState} />
        </div>
      );
    } else {
      return (
        <div>
          <ApiError errorDescription={data?.ErrorDescription}></ApiError>
        </div>
      );
    }
  }
};
